import Color from 'color';
import { lFColor, lFColor0 } from 'pages/components/LFHeader/LFHeader.styled';
import { leftSidePanelMotionEase, logRes1 } from 'pages/styles';
import styled, { keyframes } from 'styled-components';

const ink = '#212529';
const inkMuted = '#6e6e6e';
const line = Color('#e5e7eb').darken(0.02).toString();
const iconWash = Color(lFColor0).alpha(0.1).toString();

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const Root = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 24px 20px 40px;
  box-sizing: border-box;
  animation: ${fadeUp} 280ms ${leftSidePanelMotionEase};
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
  @media (max-width: ${logRes1}) {
    padding: 20px 14px 32px;
  }
`;

export const Card = styled.div`
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;
`;

export const IconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: ${iconWash};
  color: ${Color(lFColor0).alpha(0.7).toString()};
  margin-bottom: 4px;
`;

export const Title = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: ${Color(ink).toString()};
  line-height: 1.3;
`;

export const Lead = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.45;
  color: ${Color(inkMuted).toString()};
  max-width: 360px;
`;

export const Summary = styled.dl`
  width: 100%;
  margin: 6px 0 0;
  padding: 0;
  box-sizing: border-box;
  border: 1px solid ${line};
  border-radius: 12px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  text-align: left;
  overflow: hidden;
`;

export const SummaryRow = styled.div<{ $index: number }>`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  min-width: 0;
  padding: 12px 14px;
  animation: ${fadeUp} 280ms ${leftSidePanelMotionEase} both;
  animation-delay: ${({ $index }) => `${$index * 40}ms`};
  & + & {
    border-top: 1px solid ${line};
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const SummaryIcon = styled.div`
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${iconWash};
  color: ${lFColor0};
  margin-top: 1px;
`;

export const SummaryText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

export const SummaryLabel = styled.dt`
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${inkMuted};
  line-height: 1.2;
`;

export const SummaryValue = styled.dd<{ $maxLines?: number }>`
  margin: 0;
  min-width: 0;
  font-size: 13px;
  line-height: 1.4;
  color: ${ink};
  ${({ $maxLines }) => ($maxLines ? `
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${$maxLines};
    overflow: hidden;
    overflow-wrap: anywhere;
  ` : '')}
`;

export const SummaryBadges = styled.dd`
  margin: 0;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const SummarySearchTerm = styled.dd`
  margin: 0;
  min-width: 0;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.4;
  color: ${ink};
  overflow-wrap: anywhere;
  word-break: break-word;

  & + ${SummaryBadges} {
    margin-top: 4px;
  }
`;

/** Top-left search-dropdown criteria — light blue so they read as active filters. */
export const SummaryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 3px 9px;
  border-radius: 999px;
  border: 1px solid ${Color(lFColor0).alpha(0.22).toString()};
  background: ${Color(lFColor0).alpha(0.1).toString()};
  color: ${lFColor0};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-top: 6px;
  width: 100%;
`;

export const PrimaryBtn = styled.button`
  appearance: none;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: ${lFColor};
  color: #fff;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 10px 18px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 120ms ease, transform 120ms ${leftSidePanelMotionEase};
  &:hover {
    background: ${lFColor0};
  }
  &:active {
    transform: translateY(1px);
  }
  &:disabled {
    opacity: 0.65;
    cursor: default;
    transform: none;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: background 120ms ease;
    &:active {
      transform: none;
    }
  }
`;

export const SecondaryBtn = styled(PrimaryBtn)`
  background: transparent;
  color: ${lFColor};
  border: 1px solid ${Color(lFColor0).alpha(0.45).toString()};
  &:hover {
    background: ${Color(lFColor0).alpha(0.08).toString()};
  }
`;

/** Return to the loaded logs table after New search. */
export const ExitToLogsBtn = styled(SecondaryBtn)`
  width: 100%;
`;

/** Results-pane return to criteria — filled brand blue. */
export const NewSearchBtn = styled(PrimaryBtn)`
  background: ${lFColor0};
  &:hover {
    background: ${lFColor};
  }
`;

export const LoaderSlot = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  height: 100%;
  position: relative;
`;

export const ResultsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
  flex-wrap: wrap;
  padding: 8px 12px 4px;
  box-sizing: border-box;
  animation: ${fadeUp} 220ms ${leftSidePanelMotionEase};
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const ResultsControls = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1 1 280px;
  min-width: 0;
  justify-content: flex-end;
`;

export const CardControls = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
  text-align: left;
`;

export const CardControlsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  width: 100%;
`;
